import { isNil } from "@clipboard-health/util-ts";
import { hashQueryKey, type QueryKey, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ModalStatus, type UseModalState, useModalState } from "@src/lib/ui-react";

interface Props {
  children: ReactNode;
}

interface HomeHealthListRefetchActions {
  openActionDialog: (dialogId: symbol) => void;
  closeActionDialog: (dialogId: symbol) => void;
  invalidateListQuery: (queryKey: QueryKey) => void;
  registerListQuery: (queryKey: QueryKey) => () => void;
}

interface RegisteredListQuery {
  observerCount: number;
  queryKey: QueryKey;
}

const HomeHealthListRefetchPauseContext = createContext(false);
const HomeHealthListRefetchActionsContext = createContext<HomeHealthListRefetchActions | undefined>(
  undefined
);

export function HomeHealthListRefetchGuardProvider({ children }: Props) {
  const queryClient = useQueryClient();
  const openActionDialogIdsRef = useRef(new Set<symbol>());
  const deferredQueryKeysRef = useRef(new Map<string, QueryKey>());
  const registeredListQueriesRef = useRef(new Map<string, RegisteredListQuery>());
  const [isListRefetchPaused, setIsListRefetchPaused] = useState(false);

  const openActionDialog = useCallback(
    (dialogId: symbol) => {
      if (openActionDialogIdsRef.current.size === 0) {
        const cancellations = [...registeredListQueriesRef.current.values()].map(({ queryKey }) =>
          queryClient.cancelQueries({ queryKey, exact: true })
        );
        void Promise.all(cancellations);
      }

      openActionDialogIdsRef.current.add(dialogId);
      setIsListRefetchPaused(true);
    },
    [queryClient]
  );

  const closeActionDialog = useCallback(
    (dialogId: symbol) => {
      openActionDialogIdsRef.current.delete(dialogId);

      if (openActionDialogIdsRef.current.size > 0) {
        return;
      }

      setIsListRefetchPaused(false);
      const deferredQueryKeys = [...deferredQueryKeysRef.current.values()];
      deferredQueryKeysRef.current.clear();
      deferredQueryKeys.forEach((queryKey) => {
        void queryClient.invalidateQueries(queryKey);
      });
    },
    [queryClient]
  );

  const invalidateListQuery = useCallback(
    (queryKey: QueryKey) => {
      if (openActionDialogIdsRef.current.size > 0) {
        deferredQueryKeysRef.current.set(hashQueryKey(queryKey), queryKey);
        return;
      }

      void queryClient.invalidateQueries(queryKey);
    },
    [queryClient]
  );

  const registerListQuery = useCallback((queryKey: QueryKey) => {
    const queryHash = hashQueryKey(queryKey);
    const registeredQuery = registeredListQueriesRef.current.get(queryHash);
    registeredListQueriesRef.current.set(queryHash, {
      observerCount: (registeredQuery?.observerCount ?? 0) + 1,
      queryKey,
    });

    return () => {
      const currentQuery = registeredListQueriesRef.current.get(queryHash);
      if (isNil(currentQuery) || currentQuery.observerCount === 1) {
        registeredListQueriesRef.current.delete(queryHash);
        return;
      }

      registeredListQueriesRef.current.set(queryHash, {
        ...currentQuery,
        observerCount: currentQuery.observerCount - 1,
      });
    };
  }, []);

  const value = useMemo(
    () => ({
      openActionDialog,
      closeActionDialog,
      invalidateListQuery,
      registerListQuery,
    }),
    [closeActionDialog, invalidateListQuery, openActionDialog, registerListQuery]
  );

  return (
    <HomeHealthListRefetchActionsContext.Provider value={value}>
      <HomeHealthListRefetchPauseContext.Provider value={isListRefetchPaused}>
        {children}
      </HomeHealthListRefetchPauseContext.Provider>
    </HomeHealthListRefetchActionsContext.Provider>
  );
}

export function useIsHomeHealthListRefetchPaused() {
  return useContext(HomeHealthListRefetchPauseContext);
}

export function useRegisterHomeHealthListQuery({
  isGuarded,
  queryKey,
}: {
  isGuarded: boolean;
  queryKey: QueryKey;
}) {
  const registerListQuery = useContext(HomeHealthListRefetchActionsContext)?.registerListQuery;
  const queryHash = hashQueryKey(queryKey);
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  useEffect(() => {
    if (!isGuarded) {
      return;
    }

    return registerListQuery?.(queryKeyRef.current);
  }, [isGuarded, queryHash, registerListQuery]);
}

export function useInvalidateHomeHealthListQuery() {
  const queryClient = useQueryClient();
  const invalidateListQuery = useContext(HomeHealthListRefetchActionsContext)?.invalidateListQuery;

  return useCallback(
    (queryKey: QueryKey) => {
      if (invalidateListQuery) {
        invalidateListQuery(queryKey);
        return;
      }

      void queryClient.invalidateQueries(queryKey);
    },
    [invalidateListQuery, queryClient]
  );
}

export function useHomeHealthActionDialogState(
  initialModalStatus = ModalStatus.CLOSED
): UseModalState {
  const modalState = useModalState(initialModalStatus);
  const guard = useContext(HomeHealthListRefetchActionsContext);
  const dialogId = useRef(Symbol("home-health-action-dialog")).current;
  const openActionDialog = guard?.openActionDialog;
  const closeActionDialog = guard?.closeActionDialog;

  const openModal = useCallback(() => {
    openActionDialog?.(dialogId);
    modalState.openModal();
  }, [dialogId, modalState, openActionDialog]);

  const closeModal = useCallback(() => {
    modalState.closeModal();
    closeActionDialog?.(dialogId);
  }, [closeActionDialog, dialogId, modalState]);

  const setModalStatus = useCallback(
    (modalStatus: ModalStatus) => {
      if (modalStatus === ModalStatus.OPEN) {
        openModal();
        return;
      }

      closeModal();
    },
    [closeModal, openModal]
  );

  useEffect(() => {
    if (initialModalStatus === ModalStatus.OPEN) {
      openActionDialog?.(dialogId);
    }

    return () => closeActionDialog?.(dialogId);
  }, [closeActionDialog, dialogId, initialModalStatus, openActionDialog]);

  return {
    ...modalState,
    openModal,
    closeModal,
    setModalStatus,
  };
}
